import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { Chat } from "~/entities/chat";
import { chatDetailKey } from "~/entities/chat/useActiveChat";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { useCreateChat } from "./useCreateChat";

export interface DraftChatInput {
    workspaceId: string;
    projectId?: string;
    sceneId?: string;
    title?: string;
}

export interface DraftChatResult {
    chat: Chat;
    reused: boolean;
}

export interface UseGetOrCreateDraftChatResult {
    run: (input: DraftChatInput) => Promise<DraftChatResult>;
    isPending: boolean;
}

// В каждом workspace может висеть только один «draft» чат — пустой,
// только что созданный. Этот хук возвращает существующий draft,
// если он жив в кэше списка чатов; иначе создаёт новый и помечает
// его как draft. Позволяет не плодить пустые чаты при повторных
// quick-action кликах.
export function useGetOrCreateDraftChat(): UseGetOrCreateDraftChatResult {
    const qc = useQueryClient();
    const createChat = useCreateChat();
    const setDraft = useDraftChatStore((s) => s.setDraft);
    const clearDraft = useDraftChatStore((s) => s.clearDraft);

    const run = useCallback(
        async ({ workspaceId, projectId, sceneId, title }: DraftChatInput): Promise<DraftChatResult> => {
            const draftId = useDraftChatStore.getState().getDraft(workspaceId);
            const existing = draftId ? qc.getQueryData<Chat>(chatDetailKey(draftId)) : undefined;

            // Re-use draft только если он висит в тот же проект/сцену.
            // Иначе draft из другого scope «утянет» новый чат не туда.
            if (existing) {
                const sameProject = (existing.projectId ?? undefined) === projectId;
                const sameScene = (existing.sceneId ?? undefined) === sceneId;
                if (sameProject && sameScene) return { chat: existing, reused: true };
            }

            if (draftId && !existing) clearDraft(workspaceId);

            const chat = await createChat.mutateAsync({
                workspaceId,
                projectId,
                sceneId,
                title,
            });
            setDraft(workspaceId, chat.id);
            return { chat, reused: false };
        },
        [qc, createChat, setDraft, clearDraft],
    );

    return { run, isPending: createChat.isPending };
}
